import type { AgentDraft } from "@nemo/agent-schema";
import { LIMITS } from "../draft.js";
import type { FieldErrors } from "../draft.js";
import type { Api, ModelRegistry } from "../api.js";
import { LegalNoticesEditor } from "./LegalNoticesEditor.js";
import { useI18n } from "../i18n.js";
import { ExampleButtons } from "../readiness/Examples.js";
import { PANEL } from "../readiness/text.js";
import { AvatarEditor } from "./AvatarEditor.js";
import { Field, ListEditor, Section, Slider, TextInput, Toggle } from "./ui.js";

interface Props {
  form: AgentDraft;
  update: (fn: (f: AgentDraft) => AgentDraft) => void;
  errors: FieldErrors;
  models: ModelRegistry | null;
  /** With these, the picture can be uploaded and framed here; without them only an address can be pasted. */
  api?: Api;
  agentId?: string;
}

const key = (m: { provider: string; model: string }) => `${m.provider}:${m.model}`;

export function PlanStep({ form, update, errors, models, api, agentId }: Props) {
  const { t, lang } = useI18n();
  const set = <K extends keyof AgentDraft>(k: K, v: AgentDraft[K]) => update((f) => ({ ...f, [k]: v }));
  const greetings = form.greetings ?? [];

  const pickModel = (slot: "generative", value: string) => {
    const opt = models?.[slot].find((m) => key(m) === value);
    update((f) => {
      const model = { ...(f.model ?? {}) } as Record<string, unknown>;
      if (opt) model[slot] = { provider: opt.provider, model: opt.model };
      else delete model[slot];
      return { ...f, model: model as AgentDraft["model"] };
    });
  };

  return (
    <div className="step">
      <Section title="Who is your agent?">
        <div className="grid2">
          <TextInput label="Agent name" required max={LIMITS.nameMax} value={form.name ?? ""} onChange={(v) => set("name", v)} error={errors.name} placeholder="e.g. Vera" help="Visitors see this at the top of the chat." />
          <TextInput label="Creator name" required value={form.creatorName ?? ""} onChange={(v) => set("creatorName", v)} error={errors.creatorName} placeholder="Your company or your name" />
        </div>
        {api && agentId ? (
          <Field label={t("avatar.upload")}>
            <AvatarEditor api={api} agentId={agentId} name={form.name ?? ""} value={form.avatarUrl} onChange={(url) => set("avatarUrl", url)} />
            <details className="where">
              <summary>{t("avatar.or")}</summary>
              <TextInput label={t("avatar.urlLabel")} type="url" value={form.avatarUrl ?? ""} onChange={(v) => set("avatarUrl", v || undefined)} error={errors.avatarUrl} placeholder="https://example.com/avatar.png" help={t("avatar.urlHelp")} />
            </details>
          </Field>
        ) : (
          <TextInput label="Avatar image URL" type="url" value={form.avatarUrl ?? ""} onChange={(v) => set("avatarUrl", v || undefined)} error={errors.avatarUrl} placeholder="https://example.com/avatar.png" help="Optional. A square image works best." />
        )}
        <TextInput
          label="Job, skills and personality"
          required
          multiline
          rows={6}
          max={LIMITS.personaMax}
          value={form.persona ?? ""}
          onChange={(v) => set("persona", v)}
          error={errors.persona}
          help={PANEL.help.persona[lang]}
        />
        <ExampleButtons field="persona" value={form.persona ?? ""} onPick={(v) => set("persona", v)} />
      </Section>

      <Section title="First impression">
        <ListEditor
          label="Greeting message"
          required
          items={greetings}
          onChange={(v) => set("greetings", v)}
          maxLength={LIMITS.greetingMax}
          placeholder="Hi! I'm Vera. How can I help?"
          addLabel="Add greeting"
          errors={errors}
          errorPrefix="greetings"
          help="The first thing visitors read. Add more than one to show several messages."
        />
        <ListEditor
          label="Conversation starters"
          items={form.conversationStarters ?? []}
          onChange={(v) => set("conversationStarters", v)}
          max={LIMITS.startersMax}
          maxLength={LIMITS.starterMax}
          placeholder="What are your opening hours?"
          addLabel="Add"
          errors={errors}
          errorPrefix="conversationStarters"
          help={`Up to ${LIMITS.startersMax} suggested questions. Clicking one sends it. FAQs marked “chat initiator” count here too.`}
        />
        <LegalNoticesEditor form={form} update={update} errors={errors} />
      </Section>

      <Section title="How it answers">
        <div className="grid2">
          <Field label="Generative model" required htmlFor="gen-model" help="The model that writes the answers." error={errors["model.generative"]}>
            <select id="gen-model" value={form.model?.generative ? key(form.model.generative) : ""} onChange={(e) => pickModel("generative", e.target.value)}>
              <option value="">Select a model</option>
              {models?.generative.map((m) => (
                <option key={key(m)} value={key(m)}>
                  {m.label}
                  {m.default ? " (recommended)" : ""}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid2">
          <Slider
            id="creativity"
            label="Creativity"
            value={form.creativity ?? 20}
            onChange={(v) => set("creativity", v)}
            low="Stays close to the wording of your sources"
            high="Free to rephrase and be inventive"
            help={PANEL.help.creativity[lang]}
          />
          <Slider
            id="strictness"
            label="Strictness"
            value={form.strictness ?? 50}
            onChange={(v) => set("strictness", v)}
            low="May use general knowledge"
            high="Answers only from your knowledge"
            help={PANEL.help.strictness[lang]}
          />
        </div>
        <TextInput
          label="Response instructions"
          required
          multiline
          rows={5}
          max={LIMITS.responseInstructionsMax}
          value={form.responseInstructions ?? ""}
          onChange={(v) => set("responseInstructions", v)}
          error={errors.responseInstructions}
          placeholder="E.g., Reply with a friendly and professional tone."
          help={PANEL.help.responseInstructions[lang]}
        />
        <ExampleButtons field="responseInstructions" value={form.responseInstructions ?? ""} onPick={(v) => set("responseInstructions", v)} />
      </Section>

      <Section title="Notifications and privacy">
        <ListEditor
          label="Additional emails"
          items={form.additionalEmails ?? []}
          onChange={(v) => set("additionalEmails", v)}
          max={LIMITS.additionalEmailsMax}
          type="email"
          placeholder="cc@example.com"
          addLabel="Add"
          errors={errors}
          errorPrefix="additionalEmails"
          help="People who also get this agent's notifications."
        />
        <Toggle label="Allow conversations to improve AI models" checked={form.allowModelTraining ?? false} onChange={(v) => set("allowModelTraining", v)} help="Off by default. Your customers' conversations are not used to train any model unless you turn this on." />
      </Section>
    </div>
  );
}
